import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import api from '@/api/client'
import { getAccessToken } from '@/api/client'
import type { PaginatedResponse } from '@/api/client'
import type { LobbyMessage } from '@nhex/shared'
import { useAuthStore } from '@/stores/auth'
import type { SiteRole } from '@/services/rolePresentation'

const CHAT_POLL_INTERVAL_MS = 2_000
const TABLES_POLL_INTERVAL_MS = 3_000
const ONLINE_POLL_INTERVAL_MS = 1_000
const HEARTBEAT_INTERVAL_MS = 2_000
const FRIENDS_POLL_INTERVAL_MS = 5_000
const CATALOG_POLL_INTERVAL_MS = 30_000

function delay(ms: number): Promise<void> {
  return new Promise(resolve => window.setTimeout(resolve, ms))
}

function isTransientRequestError(error: unknown): boolean {
  const status = (error as { response?: { status?: number } })?.response?.status
  return status === undefined || status >= 500
}

/**
 * Store lobby — stoły, gracze online, globalny czat, heartbeat.
 * Odwzorowuje funkcjonalność oryginalnego index.html (4001 linii).
 */

/* ==================== TYPY ==================== */

export interface OnlineUser {
  id: string
  username: string
  role: SiteRole
  is_friend: boolean
  is_staff: boolean
  activity: PresenceActivity
  current_table?: string | null
  connected_at?: string
}

export type PresenceActivity = 'available' | 'table' | 'armies' | 'unavailable'

export interface PresenceState {
  activity: PresenceActivity
  tableId?: string | null
}

export interface TableInfo {
  id: string
  name: string
  owner_name: string
  owner_id: string
  owner_role: SiteRole
  board_id: string
  has_password: boolean
  visibility: 'public' | 'private'
  game_mode: '1v1' | '2v2'
  allow_friends: boolean
  player_count: number
  spectator_count: number
  max_players: number
  created_at: string
  seats: TableSeat[]
  spectator_link?: string
  is_whitelisted?: boolean
  password_verified?: boolean
}

export interface TableSeat {
  index: number
  player_name: string | null
  role: SiteRole
  link_invitation?: string
}

export interface GlobalChatMessage {
  id: string
  user_id: string
  username: string
  message: string
  timestamp: string
  role?: SiteRole
}

export interface GlobalChatPolicyMessage {
  enabled: boolean
}

export interface FriendRequest {
  id: string
  from_user_id: string
  from_username: string
  created_at: string
}

export interface ServerInfo {
  tss_ws_url: string
  armies: ArmyMeta[]
  boards: BoardMeta[]
  emotes: EmoteMeta[]
  links: ContentLink[]
  footer_links: FooterLink[]
  keymap: KeymapEntry[]
}

export interface KeymapEntry {
  id: string
  key: string
  label_pl: string
  label_en: string
}

export interface ContentLink {
  id: string
  name: string
  name_pl: string
  name_eng: string
  url_pl: string
  url_en: string
  source_pl?: 'url' | 'pdf' | ''
  source_en?: 'url' | 'pdf' | ''
  pdf_pl?: string
  pdf_en?: string
}

export interface FooterLink {
  id: string
  name: string
  name_pl: string
  name_eng: string
  title: string
  url: string
  system_key?: 'tutorial' | 'terms' | 'support'
}

export interface ArmyMeta {
  id: string
  name: string
  name_pl?: string
  name_eng?: string
  icon_url?: string
  owner_id?: string
  custom?: boolean
  private?: boolean
  readonly?: boolean
  balanced?: boolean
  utility?: boolean
  official?: boolean
  is_own?: boolean
  menu_group?: 'official' | 'fan' | 'private' | 'balanced' | 'extras' | 'utility'
  spawner_image?: string
  key_shortcut?: string
  primary_color?: string
  secondary_color?: string
}

export interface BoardMeta {
  id: string
  name: string
  image_url: string
}

export interface EmoteMeta {
  id: string
  name: string
  image_url: string
  images?: string[]
  key_shortcut?: string
  cursor_offset_x?: number
  cursor_offset_y?: number
}

export const useLobbyStore = defineStore('lobby', () => {
  const API_BASE = import.meta.env.VITE_API_URL || '/api'
  const auth = useAuthStore()

  /* ==================== STAN ==================== */
  const tables = ref<TableInfo[]>([])
  const onlineUsers = ref<OnlineUser[]>([])
  const chatMessages = ref<GlobalChatMessage[]>([])
  const globalChatEnabled = ref(true)
  const friendRequests = ref<FriendRequest[]>([])
  const serverInfo = ref<ServerInfo | null>(null)
  const loading = ref(false)
  const chatLoading = ref(false)
  const onlineLoading = ref(false)
  const onlineUpdatedAt = ref<string | null>(null)

  /* Interwały pollingu */
  let heartbeatInterval: ReturnType<typeof setInterval> | null = null
  let chatPollInterval: ReturnType<typeof setInterval> | null = null
  let chatEventSource: EventSource | null = null
  let tablesPollInterval: ReturnType<typeof setInterval> | null = null
  let onlinePollInterval: ReturnType<typeof setInterval> | null = null
  let friendsPollInterval: ReturnType<typeof setInterval> | null = null
  let catalogPollInterval: ReturnType<typeof setInterval> | null = null
  let lobbySocket: WebSocket | null = null
  let lobbyReconnectTimer: ReturnType<typeof setTimeout> | null = null
  let lobbyRealtimeEnabled = false
  let lifecycleGeneration = 0
  let presenceState: PresenceState = { activity: 'unavailable', tableId: null }
  let heartbeatInFlight = false
  let heartbeatRefreshPending = false
  let heartbeatAbortController: AbortController | null = null
  let presenceListenersAttached = false
  const presenceTabID = crypto.randomUUID()
  const presenceLeaderKey = 'nhex.presence.activeTab.v1'

  /* Guardy przed nakładaniem requestów przy wolniejszej sieci/API */
  let tablesFetchInFlight = false
  let tablesRefreshPending = false
  let onlineFetchInFlight = false
  let onlineRefreshPending = false
  let chatFetchInFlight = false
  let friendsFetchInFlight = false
  let serverInfoFetchPromise: Promise<void> | null = null
  let serverInfoRefreshPending = false

  function hasAuthToken(): boolean {
    return auth.isAuthenticated && Boolean(getAccessToken())
  }

  function stopLobbyServices(): void {
    stopChatRealtime()
    stopChatPolling()
    stopTablesPolling()
    stopOnlinePolling()
    stopFriendsPolling()
    stopCatalogRefresh()
    stopLobbyRealtime()
  }

  function stopAllPolling(): void {
    stopHeartbeat()
    stopLobbyServices()
  }

  function handleUnauthorized(error: unknown): boolean {
    const status = (error as { response?: { status?: number } })?.response?.status
    if (status === 401) {
      lifecycleGeneration += 1
      stopAllPolling()
      auth.invalidateSession()
      return true
    }
    return false
  }

  function normalizeTssUrl(rawUrl: string): string {
    let url = rawUrl.trim()
    if (!url) return url

    if (url.startsWith('/')) {
      if (typeof window === 'undefined') return url
      const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws'
      return `${protocol}://${window.location.host}${url}`
    }

    if (url.startsWith('http://')) {
      url = `ws://${url.slice('http://'.length)}`
    } else if (url.startsWith('https://')) {
      url = `wss://${url.slice('https://'.length)}`
    }

    if (typeof window !== 'undefined' && window.location.protocol === 'https:' && url.startsWith('ws://')) {
      url = `wss://${url.slice('ws://'.length)}`
    }

    return url
  }

  function normalizeChatMessage(message: GlobalChatMessage): GlobalChatMessage {
    return {
      ...message,
      id: String(message.id),
      user_id: message.user_id ? String(message.user_id) : '',
      timestamp: message.timestamp ?? '',
    }
  }

  function appendChatMessages(messages: GlobalChatMessage[]): void {
    if (messages.length === 0) return
    const existingIds = new Set(chatMessages.value.map(m => m.id))
    const newMsgs = messages
      .map(normalizeChatMessage)
      .filter(m => !existingIds.has(m.id))
    if (newMsgs.length === 0) return
    chatMessages.value.push(...newMsgs)
    if (chatMessages.value.length > 200) {
      chatMessages.value = chatMessages.value.slice(-200)
    }
  }

  /* ==================== GETTERY ==================== */
  const onlineCount = computed(() => onlineUsers.value.length)
  const tssUrl = computed(() => normalizeTssUrl(serverInfo.value?.tss_ws_url ?? 'ws://localhost:3001'))
  const hasPendingRequests = computed(() => friendRequests.value.length > 0)

  function getLobbySocketUrl(): string {
    const parsed = new URL(tssUrl.value, window.location.origin)
    parsed.pathname = '/lobby-ws'
    parsed.search = ''
    const token = getAccessToken()
    if (token) parsed.searchParams.set('token', token)
    return parsed.toString()
  }

  function retireLobbySocket(socket: WebSocket): void {
    socket.onmessage = null
    socket.onclose = null
    socket.onerror = null
    if (socket.readyState === WebSocket.CONNECTING) {
      socket.onopen = () => socket.close(1000, 'Lobby view closed')
    } else if (socket.readyState === WebSocket.OPEN) {
      socket.onopen = null
      socket.close(1000, 'Lobby view closed')
    } else {
      socket.onopen = null
    }
  }

  function startLobbyRealtime(): void {
    lobbyRealtimeEnabled = true
    if (!hasAuthToken() || !serverInfo.value || lobbySocket) return

    try {
      const socket = new WebSocket(getLobbySocketUrl())
      lobbySocket = socket
      socket.onopen = () => {
        if (lobbySocket !== socket || !lobbyRealtimeEnabled) {
          retireLobbySocket(socket)
          return
        }
        void fetchOnlineUsers()
        void fetchServerInfo()
        socket.send(JSON.stringify({
          subscribe: 'lobby',
          userId: auth.userId,
          userName: auth.username,
          accountId: auth.userId,
        }))
      }
      socket.onmessage = (event) => {
        try {
          const message = JSON.parse(String(event.data)) as LobbyMessage
          if (message.removeTable) {
            tables.value = tables.value.filter(table => table.id !== message.removeTable!.id)
          }
          if (message.tables || message.addTable || message.updateTable ||
              message.addPlayer || message.removePlayer || message.playerOffline || message.playerOnline) {
            void fetchTables()
          }
          if (message.onlineUsers || message.addOnlineUser || message.updateOnlineUser || message.removeOnlineUser) {
            // TSS informs us that presence changed. The backend heartbeat record
            // remains authoritative, especially when one account has many tabs.
            void fetchOnlineUsers()
          }
          if (message.globalChat) {
            appendChatMessages(message.globalChat.map(item => ({
              id: String(item.id),
              user_id: '',
              username: item.username,
              message: item.message,
              timestamp: '',
              role: item.role as SiteRole | undefined,
            })))
          }
          if (message.globalChatReset) {
            chatMessages.value = []
            void fetchChat()
          }
          if (message.globalChatDeleted) {
            const deletedId = String(message.globalChatDeleted.id)
            chatMessages.value = chatMessages.value.filter(item => item.id !== deletedId)
          }
          if (message.armyCatalogChanged || message.serverInfoChanged) {
            void fetchServerInfo()
          }
        } catch { /* ignore malformed realtime event */ }
      }
      socket.onclose = () => {
        if (lobbySocket === socket) lobbySocket = null
        if (lobbyRealtimeEnabled && hasAuthToken()) {
          startOnlinePolling()
          lobbyReconnectTimer = setTimeout(startLobbyRealtime, 2_000)
        }
      }
      socket.onerror = () => {
        if (lobbySocket === socket && socket.readyState === WebSocket.OPEN) socket.close()
      }
    } catch {
      lobbySocket = null
      if (lobbyRealtimeEnabled) {
        startOnlinePolling()
        lobbyReconnectTimer = setTimeout(startLobbyRealtime, 2_000)
      }
    }
  }

  function stopLobbyRealtime(): void {
    lobbyRealtimeEnabled = false
    if (lobbyReconnectTimer) {
      clearTimeout(lobbyReconnectTimer)
      lobbyReconnectTimer = null
    }
    if (lobbySocket) {
      const socket = lobbySocket
      lobbySocket = null
      retireLobbySocket(socket)
    }
  }

  function isTablePasswordVerified(tableId: string): boolean {
    return tables.value.find(table => table.id === tableId)?.password_verified === true
  }

  /* ==================== STOŁY ==================== */

  /** Pobierz listę stołów (z metadanymi od TSS) */
  async function fetchTables(): Promise<void> {
    if (!hasAuthToken()) {
      stopTablesPolling()
      return
    }
    if (tablesFetchInFlight) {
      tablesRefreshPending = true
      return
    }
    tablesFetchInFlight = true
    loading.value = true
    try {
      interface RawChair {
        id: string
        name: string | null
        kind: 'p' | 's'
        role: string
        site_role?: SiteRole
        link_invitation: string | null
      }
      interface RawTable {
        id: string
        name: string
        owner_id: string
        owner_name: string
        owner_role?: SiteRole
        board_id: string
        visibility: 'public' | 'private'
        game_mode: '1v1' | '2v2'
        max_players: number
        has_password: boolean
        allow_friends: boolean
        is_whitelisted?: boolean
        password_verified?: boolean
        spectator_count?: number
        created_at: string
        updated_at: string
        chairs: RawChair[]
      }
      const { data } = await api.get<RawTable[]>('/tables')
      tables.value = data.map((raw) => {
        const playerChairs = raw.chairs.filter(ch => ch.kind === 'p')
        const spectatorChairs = raw.chairs.filter(ch => ch.kind === 's')
        return {
          id: raw.id,
          name: raw.name,
          owner_id: raw.owner_id,
          owner_name: raw.owner_name ?? '',
          owner_role: raw.owner_role ?? 'player',
          board_id: raw.board_id,
          visibility: raw.visibility,
          game_mode: raw.game_mode,
          max_players: raw.max_players,
          has_password: raw.has_password,
          allow_friends: raw.allow_friends,
          is_whitelisted: raw.is_whitelisted ?? false,
          password_verified: raw.password_verified === true,
          created_at: raw.created_at,
          player_count: playerChairs.filter(ch => ch.name).length,
          spectator_count: raw.spectator_count ?? spectatorChairs.filter(ch => ch.name).length,
          seats: playerChairs.map((ch, idx) => ({
            index: idx,
            player_name: ch.name,
            role: ch.site_role ?? 'player',
            link_invitation: ch.link_invitation ?? undefined,
          })),
          spectator_link: spectatorChairs[0]?.link_invitation ?? undefined,
        } as TableInfo
      })
    } catch (error) {
      if (handleUnauthorized(error)) return
      /* Cichy błąd — stoły mogą być chwilowo niedostępne */
    } finally {
      loading.value = false
      tablesFetchInFlight = false
      if (tablesRefreshPending && hasAuthToken()) {
        tablesRefreshPending = false
        queueMicrotask(() => void fetchTables())
      }
    }
  }

  /** Utwórz nowy stół */
  async function createTable(payload: {
    name: string
    board_id: string
    game_mode: string
    visibility: string
    password?: string
    whitelist?: string[]
    allow_friends?: boolean
  }): Promise<string> {
    const { data } = await api.post<{ id: string }>('/tables', payload)
    await fetchTables()
    return data.id
  }

  /** Zaktualizuj stół */
  async function updateTable(tableId: string, payload: {
    name?: string
    board_id?: string
    game_mode?: string
    visibility?: string
    password?: string
    whitelist?: string[]
    allow_friends?: boolean
  }): Promise<void> {
    await api.put(`/tables/${tableId}`, payload)
    await fetchTables()
  }

  /** Usuń stół */
  async function deleteTable(tableId: string): Promise<void> {
    await api.delete(`/tables/${tableId}`)
    tables.value = tables.value.filter((t) => t.id !== tableId)
  }

  /** Weryfikuj hasło stołu */
  async function verifyTablePassword(
    tableId: string,
    password: string,
  ): Promise<boolean> {
    try {
      await api.post(`/tables/${tableId}/verify-password`, { password })
      await fetchTables()
      return true
    } catch {
      return false
    }
  }

  /* ==================== GRACZE ONLINE ==================== */

  async function fetchOnlineUsers(): Promise<void> {
    if (!hasAuthToken()) {
      stopOnlinePolling()
      return
    }
    if (onlineFetchInFlight) {
      onlineRefreshPending = true
      return
    }
    onlineFetchInFlight = true
    if (onlineUsers.value.length === 0) onlineLoading.value = true
    try {
      do {
        onlineRefreshPending = false
        try {
          const { data } = await api.get<{ count: number; users: Array<{
            user_id: string
            username: string
            role: string
            activity?: PresenceActivity
            last_seen: string
            connected_at?: string
            current_table_id?: string
            is_friend?: boolean
          }> }>('/lobby/online')
          const users = Array.isArray(data.users) ? data.users : []
          onlineUsers.value = users.map(u => ({
            id: u.user_id,
            username: u.username,
            role: (u.role || 'player') as SiteRole,
            is_friend: u.is_friend === true,
            is_staff: u.role === 'admin' || u.role === 'moderator',
            activity: normalizePresenceActivity(u.activity, u.current_table_id),
            current_table: u.current_table_id ?? null,
            connected_at: u.connected_at,
          }))
          onlineUpdatedAt.value = new Date().toISOString()
        } catch (error) {
          if (handleUnauthorized(error)) return
          /* Keep the last authoritative list through transient failures. */
        }
      } while (onlineRefreshPending && hasAuthToken())
    } finally {
      onlineFetchInFlight = false
      onlineLoading.value = false
    }
  }

  function normalizePresenceActivity(activity: unknown, tableId?: string | null): PresenceActivity {
    if (activity === 'available' || activity === 'armies' || activity === 'unavailable') return activity
    if (activity === 'table') return tableId ? 'table' : 'unavailable'
    return tableId ? 'table' : 'available'
  }

  function normalizedPresence(next: PresenceState): PresenceState {
    const tableId = next.tableId?.trim() || null
    const activity = normalizePresenceActivity(next.activity, tableId)
    return {
      activity,
      tableId: activity === 'table' ? tableId : null,
    }
  }

  function readPresenceLeader(): { tabId: string; updatedAt: number } | null {
    try {
      const value = JSON.parse(localStorage.getItem(presenceLeaderKey) || 'null') as {
        tabId?: unknown
        updatedAt?: unknown
      } | null
      if (!value || typeof value.tabId !== 'string' || typeof value.updatedAt !== 'number') return null
      return { tabId: value.tabId, updatedAt: value.updatedAt }
    } catch {
      return null
    }
  }

  function writePresenceLeader(): void {
    try {
      localStorage.setItem(presenceLeaderKey, JSON.stringify({
        tabId: presenceTabID,
        updatedAt: Date.now(),
      }))
    } catch {
      /* localStorage unavailable: the visible tab can still report presence. */
    }
  }

  function canClaimPresence(): boolean {
    return typeof document === 'undefined' || document.visibilityState === 'visible'
  }

  function isPresenceLeader(): boolean {
    const leader = readPresenceLeader()
    if (!leader) return canClaimPresence()
    if (leader.tabId === presenceTabID) return true
    return Date.now() - leader.updatedAt > HEARTBEAT_INTERVAL_MS * 2.5 && canClaimPresence()
  }

  function claimPresence(): void {
    if (!canClaimPresence() || !hasAuthToken()) return
    writePresenceLeader()
    void sendHeartbeat(true)
  }

  function onPresenceFocus(): void {
    claimPresence()
  }

  function onPresenceVisibilityChange(): void {
    if (document.visibilityState === 'visible') claimPresence()
  }

  function onPresenceStorage(event: StorageEvent): void {
    if (event.key !== presenceLeaderKey) return
    if (!event.newValue && canClaimPresence()) {
      claimPresence()
      return
    }
    const leader = readPresenceLeader()
    if (leader && leader.tabId !== presenceTabID) {
      // Do not let a late response from the previously active tab overwrite the
      // state just claimed by another tab.
      heartbeatAbortController?.abort()
    }
  }

  function attachPresenceListeners(): void {
    if (presenceListenersAttached) return
    presenceListenersAttached = true
    window.addEventListener('focus', onPresenceFocus)
    window.addEventListener('pageshow', onPresenceFocus)
    window.addEventListener('storage', onPresenceStorage)
    document.addEventListener('visibilitychange', onPresenceVisibilityChange)
  }

  function detachPresenceListeners(): void {
    if (!presenceListenersAttached) return
    presenceListenersAttached = false
    window.removeEventListener('focus', onPresenceFocus)
    window.removeEventListener('pageshow', onPresenceFocus)
    window.removeEventListener('storage', onPresenceStorage)
    document.removeEventListener('visibilitychange', onPresenceVisibilityChange)
  }

  /** Heartbeat is deliberately shorter than the five-second presence window. */
  function startHeartbeat(next: PresenceState = presenceState): void {
    presenceState = normalizedPresence(next)
    if (!hasAuthToken()) {
      stopHeartbeat()
      return
    }
    attachPresenceListeners()
    if (!heartbeatInterval) {
      heartbeatInterval = setInterval(() => void sendHeartbeat(), HEARTBEAT_INTERVAL_MS)
    }
    if (canClaimPresence()) claimPresence()
  }

  function stopHeartbeat(): void {
    if (heartbeatInterval) {
      clearInterval(heartbeatInterval)
      heartbeatInterval = null
    }
    heartbeatRefreshPending = false
    heartbeatAbortController?.abort()
    heartbeatAbortController = null
    detachPresenceListeners()
    const leader = readPresenceLeader()
    if (leader?.tabId === presenceTabID) {
      try {
        localStorage.removeItem(presenceLeaderKey)
      } catch {
        /* localStorage unavailable */
      }
    }
  }

  async function sendHeartbeat(force = false): Promise<boolean> {
    if (!hasAuthToken()) {
      stopHeartbeat()
      return false
    }
    if (!force && !isPresenceLeader()) return true
    if (!isPresenceLeader()) {
      if (!canClaimPresence()) return true
      writePresenceLeader()
    } else {
      writePresenceLeader()
    }
    if (heartbeatInFlight) {
      heartbeatRefreshPending = true
      return true
    }
    heartbeatInFlight = true
    heartbeatRefreshPending = false
    const sentPresence = { ...presenceState }
    const controller = new AbortController()
    heartbeatAbortController = controller
    try {
      await api.post('/lobby/heartbeat', {
        activity: sentPresence.activity,
        table_id: sentPresence.tableId ?? null,
      }, { signal: controller.signal })
      return true
    } catch (error) {
      if (controller.signal.aborted) return false
      if (handleUnauthorized(error)) return false
      return false
    } finally {
      heartbeatInFlight = false
      if (heartbeatAbortController === controller) heartbeatAbortController = null
      if (heartbeatRefreshPending && hasAuthToken() && isPresenceLeader()) {
        heartbeatRefreshPending = false
        queueMicrotask(() => void sendHeartbeat())
      }
    }
  }

  /* ==================== CZAT GLOBALNY ==================== */

  async function fetchChat(): Promise<void> {
    if (!hasAuthToken()) {
      stopChatRealtime()
      stopChatPolling()
      return
    }
    if (chatFetchInFlight) return
    chatFetchInFlight = true
    try {
      const { data } = await api.get<GlobalChatMessage[]>('/chat/messages')
      chatMessages.value = data.map(normalizeChatMessage).slice(-200)
    } catch (error) {
      if (handleUnauthorized(error)) return
      /* cicho */
    }
    finally {
      chatFetchInFlight = false
    }
  }

  async function sendChatMessage(content: string): Promise<void> {
    chatLoading.value = true
    try {
      await api.post('/chat/messages', { message: content })
      await fetchChat()
    } finally {
      chatLoading.value = false
    }
  }

  async function deleteGlobalChatMessage(id: string): Promise<void> {
    const normalized = String(id).trim()
    if (!/^\d+$/.test(normalized)) throw new Error('Invalid message id')
    await api.delete(`/mod/chat/${normalized}`)
    chatMessages.value = chatMessages.value.filter(message => message.id !== normalized)
  }

  /** Polling czatu co 3s (fallback gdy SSE jest niedostępne) */
  function startChatPolling(): void {
    if (!hasAuthToken()) return
    stopChatPolling()
    fetchChat()
    chatPollInterval = setInterval(() => {
      if (typeof document !== 'undefined' && document.hidden) return
      fetchChat()
    }, CHAT_POLL_INTERVAL_MS)
  }

  function stopChatPolling(): void {
    if (chatPollInterval) {
      clearInterval(chatPollInterval)
      chatPollInterval = null
    }
  }

  /** Realtime chat przez SSE, z fallbackiem do pollingu. */
  function startChatRealtime(): void {
    if (!hasAuthToken()) return
    stopChatRealtime()

    if (typeof window === 'undefined' || typeof EventSource === 'undefined') {
      startChatPolling()
      return
    }

    if (chatMessages.value.length === 0) {
      fetchChat()
    }
    startChatPolling()

    const normalizedBase = API_BASE.endsWith('/') ? API_BASE.slice(0, -1) : API_BASE
    const streamUrl = `${normalizedBase}/chat/stream`
    const allowedStreamOrigin = (() => {
      try {
        return new URL(streamUrl, window.location.origin).origin
      } catch {
        return window.location.origin
      }
    })()
    chatEventSource = new EventSource(streamUrl, { withCredentials: true })

    chatEventSource.addEventListener('message', (event: MessageEvent) => {
      const eventOrigin = typeof event.origin === 'string' ? event.origin : ''
      if (eventOrigin && eventOrigin !== allowedStreamOrigin) {
        return
      }
      try {
        const payload = JSON.parse(event.data) as GlobalChatMessage
        appendChatMessages([payload])
      } catch {
        // Ignorujemy pojedyncze błędne eventy.
      }
    })

    chatEventSource.addEventListener('policy', (event: MessageEvent) => {
      try {
        const payload = JSON.parse(event.data) as GlobalChatPolicyMessage
        globalChatEnabled.value = payload.enabled !== false
      } catch {
        // Ignorujemy pojedyncze błędne eventy.
      }
    })

    chatEventSource.addEventListener('reset', () => {
      chatMessages.value = []
      void fetchChat()
    })

    chatEventSource.onerror = () => {
      // Przy błędach transportu przechodzimy na sprawdzony polling.
      stopChatRealtime()
      startChatPolling()
    }
  }

  function stopChatRealtime(): void {
    if (chatEventSource) {
      chatEventSource.close()
      chatEventSource = null
    }
  }

  /** Polling stołów co 8s */
  function startTablesPolling(): void {
    if (!hasAuthToken()) return
    stopTablesPolling()
    fetchTables()
    tablesPollInterval = setInterval(() => {
      if (typeof document !== 'undefined' && document.hidden) return
      fetchTables()
    }, TABLES_POLL_INTERVAL_MS)
  }

  function stopTablesPolling(): void {
    if (tablesPollInterval) {
      clearInterval(tablesPollInterval)
      tablesPollInterval = null
    }
  }

  /** One-second fallback keeps the five-second presence timeout accurate. */
  function startOnlinePolling(): void {
    if (!hasAuthToken()) return
    stopOnlinePolling()
    fetchOnlineUsers()
    onlinePollInterval = setInterval(() => {
      if (typeof document !== 'undefined' && document.hidden) return
      fetchOnlineUsers()
    }, ONLINE_POLL_INTERVAL_MS)
  }

  function stopOnlinePolling(): void {
    if (onlinePollInterval) {
      clearInterval(onlinePollInterval)
      onlinePollInterval = null
    }
  }

  /** Polling friend requests co 20s */
  function startFriendsPolling(): void {
    if (!hasAuthToken()) return
    stopFriendsPolling()
    fetchFriendRequests()
    friendsPollInterval = setInterval(() => {
      if (typeof document !== 'undefined' && document.hidden) return
      fetchFriendRequests()
    }, FRIENDS_POLL_INTERVAL_MS)
  }

  function stopFriendsPolling(): void {
    if (friendsPollInterval) {
      clearInterval(friendsPollInterval)
      friendsPollInterval = null
    }
  }

  /* ==================== ZNAJOMI ==================== */

  async function fetchFriendRequests(): Promise<void> {
    if (!hasAuthToken()) {
      stopFriendsPolling()
      return
    }
    if (friendsFetchInFlight) return
    friendsFetchInFlight = true
    try {
      const { data } = await api.get<Array<{
        id: string
        from_user: { id: string; username: string }
        created_at: string
      }>>('/social/friends/requests')
      friendRequests.value = data.map(r => ({
        id: r.id,
        from_user_id: r.from_user.id,
        from_username: r.from_user.username,
        created_at: r.created_at,
      }))
    } catch (error) {
      if (handleUnauthorized(error)) return
      /* cicho */
    }
    finally {
      friendsFetchInFlight = false
    }
  }

  async function addFriend(username: string): Promise<void> {
    await api.post('/social/friends/request', { username })
    await Promise.all([
      fetchFriendRequests(),
      fetchOnlineUsers(),
    ])
  }

  async function acceptFriendRequest(requestId: string): Promise<void> {
    await api.post(`/social/friends/requests/${requestId}/accept`)
    friendRequests.value = friendRequests.value.filter((r) => r.id !== requestId)
    await fetchOnlineUsers()
  }

  async function rejectFriendRequest(requestId: string): Promise<void> {
    await api.delete(`/social/friends/requests/${requestId}`)
    friendRequests.value = friendRequests.value.filter((r) => r.id !== requestId)
  }

  async function removeFriend(friendId: string): Promise<void> {
    await api.delete(`/social/friends/${friendId}`)
    const normalized = friendId.trim().toLowerCase()
    onlineUsers.value = onlineUsers.value.map(user =>
      user.id === friendId || user.username.toLowerCase() === normalized
        ? { ...user, is_friend: false }
        : user,
    )
  }

  /* ==================== MODERACJA ==================== */

  async function reportUser(
    userId: string,
    reason: string,
  ): Promise<void> {
    await api.post('/social/reports', { user_id: userId, reason })
  }

  /** POST /api/mod/mute — expects { username, mute_type: 'all'|'global', duration?: '30m' } */
  async function muteUser(
    username: string,
    muteType: 'all' | 'global',
    duration: string,
  ): Promise<{ message: string }> {
    const { data } = await api.post<{ message: string }>('/mod/mute', {
      username,
      mute_type: muteType,
      duration: duration || undefined,
    })
    return data
  }

  /** DELETE /api/mod/mute/:userId — unmute */
  async function unmuteUser(userId: string): Promise<{ message: string; removed: number }> {
    const { data } = await api.delete<{ message: string; removed: number }>(`/mod/mute/${userId}`)
    return data
  }

  /** POST /api/mod/ban — expects { username, reason, duration?: '2h' } */
  async function banUser(
    username: string,
    reason: string,
    duration: string,
  ): Promise<{ message: string }> {
    const { data } = await api.post<{ message: string }>('/mod/ban', {
      username,
      reason,
      duration: duration || undefined,
    })
    return data
  }

  /** DELETE /api/mod/ban/:userId — unban */
  async function unbanUser(userId: string): Promise<{ message: string }> {
    const { data } = await api.delete<{ message: string }>(`/mod/ban/${userId}`)
    return data
  }

  /* ==================== INFO SERWERA ==================== */

  async function fetchServerInfo(): Promise<void> {
    if (serverInfoFetchPromise) {
      serverInfoRefreshPending = true
      return serverInfoFetchPromise
    }

    serverInfoFetchPromise = (async () => {
      do {
        serverInfoRefreshPending = false
        for (let attempt = 0; attempt < 3; attempt += 1) {
          try {
            const { data } = await api.get<ServerInfo>('/lobby/server-info')
            serverInfo.value = data
            break
          } catch (error) {
            if (handleUnauthorized(error)) return
            if (!isTransientRequestError(error) || attempt === 2) break
            await delay(200 * (attempt + 1))
          }
        }
      } while (serverInfoRefreshPending)
    })().finally(() => {
      serverInfoFetchPromise = null
    })

    return serverInfoFetchPromise
  }

  function refreshCatalogOnFocus(): void {
    if (!document.hidden) void fetchServerInfo()
  }

  function startCatalogRefresh(): void {
    stopCatalogRefresh()
    catalogPollInterval = setInterval(() => void fetchServerInfo(), CATALOG_POLL_INTERVAL_MS)
    window.addEventListener('focus', refreshCatalogOnFocus)
  }

  function stopCatalogRefresh(): void {
    if (catalogPollInterval) {
      clearInterval(catalogPollInterval)
      catalogPollInterval = null
    }
    window.removeEventListener('focus', refreshCatalogOnFocus)
  }

  /* ==================== LIFECYCLE ==================== */

  /** Inicjalizuj lobby — pobierz dane i uruchom polling */
  async function initLobby(): Promise<void> {
    if (!hasAuthToken()) {
      stopAllPolling()
      return
    }
    const generation = ++lifecycleGeneration
    stopLobbyServices()
    await fetchServerInfo()
    if (generation !== lifecycleGeneration || !hasAuthToken()) return
    await Promise.all([
      fetchTables(),
      fetchOnlineUsers(),
      fetchChat(),
      fetchFriendRequests(),
    ])
    if (generation !== lifecycleGeneration || !hasAuthToken()) return
    startChatRealtime()
    startTablesPolling()
    startOnlinePolling()
    startFriendsPolling()
    startLobbyRealtime()
    startCatalogRefresh()
  }

  /** Zatrzymaj polling przy opuszczeniu lobby */
  function destroyLobby(): void {
    lifecycleGeneration += 1
    stopLobbyServices()
  }

  return {
    /* Stan */
    tables,
    onlineUsers,
    chatMessages,
    globalChatEnabled,
    friendRequests,
    serverInfo,
    loading,
    chatLoading,
    onlineLoading,
    onlineUpdatedAt,
    /* Gettery */
    onlineCount,
    tssUrl,
    hasPendingRequests,
    /* Akcje: stoły */
    fetchTables,
    createTable,
    updateTable,
    deleteTable,
    verifyTablePassword,
    isTablePasswordVerified,
    /* Akcje: online */
    fetchOnlineUsers,
    startHeartbeat,
    stopHeartbeat,
    /* Akcje: czat */
    fetchChat,
    sendChatMessage,
    deleteGlobalChatMessage,
    startChatRealtime,
    stopChatRealtime,
    startChatPolling,
    stopChatPolling,
    /* Akcje: znajomi */
    fetchFriendRequests,
    addFriend,
    acceptFriendRequest,
    rejectFriendRequest,
    removeFriend,
    /* Akcje: moderacja */
    reportUser,
    muteUser,
    unmuteUser,
    banUser,
    unbanUser,
    /* Akcje: server info */
    fetchServerInfo,
    startLobbyRealtime,
    stopLobbyRealtime,
    /* Lifecycle */
    initLobby,
    destroyLobby,
  }
})
